import { RolePermission, PermissionCategory } from '../types/rolePermission'
import { UserRole } from '../types/user'
import { groupPermissionsByCategory } from '../types/rolePermission'
import './RolePermissionCard.css'

interface RolePermissionCardProps {
  rolePermission: RolePermission
  onEdit: (role: UserRole) => void
  onApplyTemplate?: (role: UserRole, templateName: string) => void
  templates?: Array<{ id: string; name: string }>
  showTemplateActions?: boolean
}

export function RolePermissionCard({
  rolePermission,
  onEdit,
  onApplyTemplate,
  templates = [],
  showTemplateActions = false,
}: RolePermissionCardProps) {
  const formatRoleName = (role: UserRole): string => {
    return role
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const getRoleBadgeClass = (category: string): string => {
    switch (category) {
      case 'Clinical':
        return 'badge-clinical'
      case 'Administrative':
        return 'badge-admin'
      case 'System':
        return 'badge-system'
      default:
        return 'badge-default'
    }
  }

  const hasAllPermissions = rolePermission.permissions.includes('*')
  const groupedPermissions: Record<PermissionCategory, string[]> = hasAllPermissions
    ? {
        'Patients': [],
        'Appointments': [],
        'Templates': [],
        'Lab Results': [],
        'Billing': [],
        'Users': [],
        'Settings': [],
        'Role Permissions': [],
        'System': [],
      }
    : groupPermissionsByCategory(rolePermission.permissions)

  const permissionCount = hasAllPermissions ? 'All' : rolePermission.permissions.length

  return (
    <div className="role-permission-card">
      <div className="card-header">
        <div className="card-title-section">
          <h3 className="card-title">{formatRoleName(rolePermission.role)}</h3>
          <span className={`badge ${getRoleBadgeClass(rolePermission.roleCategory)}`}>
            {rolePermission.roleCategory}
          </span>
        </div>
        <div className="card-actions">
          {showTemplateActions && templates.length > 0 && (
            <select
              className="template-select"
              onChange={(e) => {
                if (e.target.value && onApplyTemplate) {
                  onApplyTemplate(rolePermission.role, e.target.value)
                }
                e.target.value = '' // Reset select
              }}
              defaultValue=""
            >
              <option value="" disabled>
                Apply Template...
              </option>
              {templates.map((template) => (
                <option key={template.id} value={template.name}>
                  {template.name}
                </option>
              ))}
            </select>
          )}
          <button
            className="btn-edit"
            onClick={() => onEdit(rolePermission.role)}
            aria-label={`Edit permissions for ${formatRoleName(rolePermission.role)}`}
          >
            Edit
          </button>
        </div>
      </div>

      <div className="card-content">
        <div className="permission-summary">
          <span className="permission-count">
            <strong>{permissionCount}</strong> {permissionCount === 'All' ? 'permissions' : 'permission(s)'}
          </span>
        </div>

        {hasAllPermissions ? (
          <div className="permissions-display">
            <div className="permission-item all-permissions">
              <code className="permission-code">*</code>
              <span className="permission-description">All permissions (administrative access)</span>
            </div>
          </div>
        ) : (
          <div className="permissions-display">
            {Object.keys(groupedPermissions).length > 0 ? (
              (Object.keys(groupedPermissions) as PermissionCategory[]).map(
                (category) => {
                  const perms = groupedPermissions[category]
                  if (perms.length === 0) return null

                  return (
                    <div key={category} className="permission-category">
                      <div className="category-header">{category}</div>
                      <div className="category-permissions">
                        {perms.slice(0, 5).map((perm: string) => (
                          <span key={perm} className="permission-tag">
                            <code className="permission-code-small">{perm}</code>
                          </span>
                        ))}
                        {perms.length > 5 && (
                          <span className="permission-more">+{perms.length - 5} more</span>
                        )}
                      </div>
                    </div>
                  )
                }
              )
            ) : (
              <div className="no-permissions">No permissions assigned</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
